import { ChangeEvent, useEffect, useState } from 'react';
import { Avatar, Badge, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AddAPhoto } from '@mui/icons-material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { StyledInput } from '@/components/atoms/inputs/styled';
import { Text } from '@/components/atoms/text';
import { AppRouters } from '@/utils/constants/routers';
import { Modal } from '@/components/organisms/modal';
import {
  Buttons,
  CancelAndSaveButtons,
  CancelButton,
  ChangePasswordButton,
  FieldsInRow,
  Input,
  SaveButton,
  StyledAddAPhoto,
  StyledContainer,
  StyledForm,
  StyledIconButton,
  StyledPaper,
} from './styles';
import { useAuth } from '@/hooks/useAuth';
import { resetPassword, updateUserProfile } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { Alert } from '@/components/molecules/alert';
import { useAlert } from '@/hooks/useAlert';
import { PageLayout } from '@/components/molecules/pageLayout';
import { MaxContent } from '@/styles/globalStyles';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';

enum Actions {
  changeEmail,
  changePassword,
}

const threeMBInKB = 3250585.6;
const imageType = ['image/png', 'image/jpeg', 'image/jpg'];

export default function ProfilePage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  const { user, getUser } = useAuth();
  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    name: Yup.string().required(`${t('messages.error.error.name_required')}`),
    phone: Yup.string(),
    email: Yup.string(),
    avatar_url: Yup.string(),
  });

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [userPicture, setUserPicture] = useState<{
    file?: File;
    url: string;
  }>();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<Actions | undefined>();

  const isMobileVersion = useMediaQuery(theme.breakpoints.down('sm'));

  const handleModalClose = () => {
    setModalOpen(false);
    setCurrentAction(undefined);
  };

  const handleModalConfirm = async () => {
    try {
      if (user?.email && currentAction === Actions.changeEmail) {
        showAlert('success', t('messages.success.message.profile.changeEmail'));
      }
      if (user?.email && currentAction === Actions.changePassword) {
        setModalLoading(true);
        await resetPassword(user.email);
        setModalLoading(false);
        showAlert('success', t('messages.success.message.profile.changePassword'));
      }
      handleModalClose();
    } catch (error) {
      showAlert('error', t('messages.error.error.profile.changePasswordOrEmail'));
    }
  };

  const requestUpdatePasswordOrEmail = (actionName: Actions) => {
    setCurrentAction(actionName);
    setModalOpen(true);
  };

  const initialValues = {
    name: user?.displayName || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    avatar_url: user?.photoURL || '',
  };

  const uploadImage = (target: EventTarget & HTMLInputElement) => {
    if (target.files?.length && imageType.includes(target.files[0]?.type)) {
      setUserPicture({
        file: target.files[0],
        url: URL.createObjectURL(target.files[0]),
      });
      return;
    }

    if (!target.files?.length) {
      return;
    }

    if (target.files?.length && !imageType.includes(target.files[0]?.type)) {
      showAlert('error', t('messages.error.error.profile.imageFormat'));
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setSubmitLoading(true);

      if (userPicture?.file && userPicture.file.size > threeMBInKB) {
        showAlert('error', t('messages.error.error.profile.imageSize'));
        return;
      }

      await updateUserProfile(
        apolloClient,
        {
          displayName: values.name,
          ...(userPicture?.file && { photo: userPicture.file }),
        },
        getUser
      );

      showAlert('success', t('messages.success.message.profile.changeData'));
    } catch (error) {
      error instanceof Error && showAlert('error', t('messages.error.error.profile.changeData'));
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'KYT-e - Profile';
  }, []);

  return (
    <PageLayout.Root
      contentPadding={{ vertical: 2, horizontal: 2 }}
      header={
        <PageLayout.Background>
          <PageLayout.Header
            title={t('page.profile.profile.title')}
            onBack={() => navigate(AppRouters.home)}
          />
        </PageLayout.Background>
      }
    >
      <MaxContent>
        <StyledContainer>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={schema}
            enableReinitialize
          >
            {({ values, errors, handleChange }) => (
              <StyledPaper variant="outlined">
                <Form style={StyledForm}>
                  <Text variant="subtitle1" textAlign="center">
                    <SafeMarkdown text={t('page.profile.profile.picture')} />
                  </Text>

                  <Badge
                    data-testid="avatar-badge"
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <label data-testid="upload-picture-label" htmlFor="icon-button-file">
                        <Input
                          accept=".png, .jpeg, .jpg"
                          id="icon-button-file"
                          type="file"
                          onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            uploadImage(event.target)
                          }
                        />

                        <IconButton
                          data-testid="upload-picture-button"
                          component="span"
                          color="primary"
                          aria-label="upload picture"
                          sx={StyledIconButton}
                        >
                          <AddAPhoto sx={StyledAddAPhoto} />
                        </IconButton>
                      </label>
                    }
                    sx={{
                      marginTop: ({ spacing }) => spacing(1.5),
                      marginBottom: ({ spacing }) => spacing(2),
                    }}
                  >
                    <Avatar
                      data-testid="avatar-image"
                      alt={user?.displayName || ''}
                      src={userPicture?.url || values?.avatar_url || ''}
                      sx={{ width: 132, height: 132 }}
                    />
                  </Badge>

                  <Text variant="body2" secondary textAlign="center">
                    <SafeMarkdown text={t('page.profile.profile.picture.allowed')} />
                  </Text>

                  <Text variant="body2" secondary textAlign="center">
                    <SafeMarkdown text={t('page.profile.profile.picture.max')} />
                  </Text>

                  <StyledInput
                    name="name"
                    label={t('label.label.full_name')}
                    margin="normal"
                    type="text"
                    onChange={handleChange}
                    helperText={errors.name}
                    fullWidth
                    defaultValue={values.name}
                  />

                  {/* <FieldsInRow>
                  <StyledInput
                    name="phone"
                    label="Phone"
                    margin="normal"
                    type="text"
                    onChange={handleChange}
                    helperText={errors.phone}
                    fullWidth
                    defaultValue={values.phone}
                  />
                </FieldsInRow> */}

                  <FieldsInRow>
                    <StyledInput
                      name="email"
                      label={t('label.label.email')}
                      margin="normal"
                      type="email"
                      onChange={handleChange}
                      defaultValue={values.email}
                      fullWidth
                      disabled
                    />
                  </FieldsInRow>

                  <Buttons>
                    {/* <Button
                    variant="outlined"
                    type="button"
                    size={isMobileVersion ? 'small' : 'medium'}
                    onClick={() =>
                      requestUpdatePasswordOrEmail(
                        user?.email || '',
                        Actions.changeEmail
                      )
                    }
                  >
                    Change email
                  </Button> */}

                    <ChangePasswordButton
                      data-testid="change-password-button"
                      variant="outlined"
                      type="button"
                      size={isMobileVersion ? 'small' : 'medium'}
                      onClick={() => requestUpdatePasswordOrEmail(Actions.changePassword)}
                    >
                      <SafeMarkdown text={t('button.button.change_password')} />
                    </ChangePasswordButton>
                  </Buttons>

                  <CancelAndSaveButtons>
                    <CancelButton
                      data-testid="cancel-button"
                      variant="text"
                      type="button"
                      size={isMobileVersion ? 'small' : 'medium'}
                      onClick={() => navigate(AppRouters.home)}
                    >
                      <SafeMarkdown text={t('button.button.cancel')} />
                    </CancelButton>

                    <SaveButton
                      data-testid="save-button"
                      variant="contained"
                      type="submit"
                      size={isMobileVersion ? 'small' : 'medium'}
                      loading={submitLoading}
                      fullWidth={isMobileVersion ? true : false}
                      disabled={errors.name}
                    >
                      <SafeMarkdown text={t('button.button.save')} />
                    </SaveButton>
                  </CancelAndSaveButtons>
                </Form>
              </StyledPaper>
            )}
          </Formik>
        </StyledContainer>
      </MaxContent>

      {alert.open && (
        <Alert
          open={alert.open}
          severity={alert.severity}
          message={alert.message}
          autoHideDuration={5000}
          onClose={handleCloseAlert}
        />
      )}

      {modalOpen && (
        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          modalTitle={t('modal.modal.profile.title')}
          submitButton={{
            text: t('modal.modal.profile.confirmation.send'),
            onClick: handleModalConfirm,
            loading: modalLoading,
          }}
          cancelButton={{
            text: t('modal.modal.profile.confirmation.cancel'),
            onClick: handleModalClose,
          }}
        >
          <Text variant="subtitle1" textAlign="center" sx={{ mt: 2, mb: 2 }}>
            <SafeMarkdown text={t('modal.modal.profile.confirmation.title')} />
          </Text>
          <Text variant="body2" textAlign="center" secondary>
            <SafeMarkdown text={t('modal.modal.profile.confirmation.description')} />
          </Text>
        </Modal>
      )}
    </PageLayout.Root>
  );
}
